import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Calendar, Users, Plus, ChevronRight, Loader2, Sparkles, Image, FileText, MessageCircle, Download, RotateCcw, History, Share2, Wifi, WifiOff, TrendingUp, Percent, Instagram, BarChart3, Gamepad2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import SocialShare from "../components/SocialShare";
import wsService from "../lib/websocket";
import InstagramStoryExport from "../components/InstagramStoryExport";
import LeaguePerformanceGraph from "../components/LeaguePerformanceGraph";

export default function LeagueDetail() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const { user, wsConnected } = useAuth();
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [showSeasons, setShowSeasons] = useState(false);
  const [liveUpdate, setLiveUpdate] = useState(null);
  const [probability, setProbability] = useState(null);
  const [storyExportOpen, setStoryExportOpen] = useState(false);
  const [storyData, setStoryData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [leagueAdmins, setLeagueAdmins] = useState([]);
  const standingsRef = useRef(null);

  const fetchLeague = async () => {
    try {
      const response = await api.getLeague(leagueId);
      setLeague(response.data);
      
      // Check if user is admin
      if (user) {
        const adminCheck = await api.checkLeagueAdmin(leagueId);
        setIsAdmin(adminCheck.data.is_admin);
        
        const adminsRes = await api.getLeagueAdmins(leagueId);
        setLeagueAdmins(adminsRes.data.admins || []);
      }
      
      // Fetch archived seasons
      const seasonsRes = await api.getLeagueSeasons(leagueId);
      setSeasons(seasonsRes.data);
      
      // Fetch championship probability
      if (response.data.weeks?.length > 0) {
        const probRes = await api.getChampionshipProbability(leagueId);
        setProbability(probRes.data);
        
        // Fetch stats for performance graph
        setStatsLoading(true);
        const statsRes = await api.getLeagueStatistics(leagueId);
        setStatsData(statsRes.data);
        setStatsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching league:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeague();
    
    // Subscribe to league updates via WebSocket
    wsService.subscribeToLeague(leagueId);
    
    const unsubscribe = wsService.on('league_update', (data) => {
      if (data.league_id === leagueId) {
        // Show live update notification
        setLiveUpdate(data);
        toast.success(`🔴 Canlı: ${data.event === 'week_added' ? `Hafta ${data.data.week_number} eklendi!` : 'Güncelleme'}`);
        
        // Refresh data
        fetchLeague();
        
        // Clear notification after 5 seconds
        setTimeout(() => setLiveUpdate(null), 5000);
      }
    });
    
    return () => {
      unsubscribe();
      wsService.unsubscribe('league', leagueId);
    };
  }, [leagueId]);

  // Online oyun başlat
  const handleStartOnlineGame = async () => {
    try {
      // Admin ise direkt yeni oda oluştur veya mevcut bekleyen odaya git
      if (isAdmin) {
        // Önce mevcut odaları kontrol et
        try {
          const roomsRes = await api.getLeagueRooms(leagueId);
          const rooms = roomsRes.data?.rooms || [];
          
          // Bekleyen oda varsa ona yönlendir
          const waitingRoom = rooms.find(r => r.status === "waiting");
          if (waitingRoom) {
            try {
              await api.getGameRoom(waitingRoom.room_id);
              navigate(`/game/${waitingRoom.room_id}`);
              return;
            } catch (roomErr) {
              // Oda artık yok, devam et yeni oluştur
              console.log("Eski oda bulunamadı, yeni oluşturuluyor...");
            }
          }
        } catch (roomsErr) {
          // Oda listesi alınamadı, yeni oluştur
          console.log("Oda listesi alınamadı, yeni oda oluşturuluyor...");
        }
        
        // Yeni oda oluştur
        const response = await api.createGameRoom(leagueId, {});
        const roomId = response.data.room_id;
        
        // Odaya katıl
        await api.joinGameRoom(roomId, 0);
        
        toast.success("Oyun odası oluşturuldu!");
        navigate(`/game/${roomId}`);
      } else {
        // Admin değilse mevcut odaları kontrol et
        try {
          const roomsRes = await api.getLeagueRooms(leagueId);
          const rooms = roomsRes.data?.rooms || [];
          const waitingRoom = rooms.find(r => r.status === "waiting");
          
          if (waitingRoom) {
            navigate(`/game/${waitingRoom.room_id}`);
          } else {
            toast.info("Henüz aktif oyun odası yok. Lig admini oda oluşturmalı.");
          }
        } catch (err) {
          toast.info("Henüz aktif oyun odası yok. Lig admini oda oluşturmalı.");
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Online oyun başlatılamadı");
    }
  };

  const exportAsImage = async () => {
    if (!standingsRef.current) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(standingsRef.current, {
        backgroundColor: "#050E2E",
        scale: 2
      });
      
      const link = document.createElement("a");
      link.download = `${league.name.replace(/\s+/g, "_")}_puan_durumu.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      
      toast.success("Görsel indirildi");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Görsel oluşturulamadı");
    } finally {
      setExporting(false);
    }
  };

  const exportAsPDF = async () => {
    if (!standingsRef.current) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(standingsRef.current, {
        backgroundColor: "#050E2E",
        scale: 2
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? "landscape" : "portrait",
        unit: "px",
        format: [canvas.width, canvas.height]
      });
      
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save(`${league.name.replace(/\s+/g, "_")}_puan_durumu.pdf`);
      
      toast.success("PDF indirildi");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("PDF oluşturulamadı");
    } finally {
      setExporting(false);
    }
  };

  const sendWhatsAppSummary = async () => {
    setSendingWhatsApp(true);
    try {
      const response = await api.sendLeagueWhatsAppSummary(leagueId);
      
      if (response.data?.results?.some(r => r.mock)) {
        toast.info("WhatsApp paylaşımı mock modda. Twilio API anahtarları eklendiğinde gerçek mesajlar gönderilecek.", {
          duration: 5000
        });
      } else {
        toast.success("WhatsApp özeti gönderildi!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "WhatsApp özeti gönderilemedi");
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleResetSeason = async () => {
    setResetting(true);
    try {
      const response = await api.resetLeagueSeason(leagueId);
      toast.success(`Sezon ${response.data.season_number} arşivlendi, yeni sezon başladı!`);
      setResetDialogOpen(false);
      fetchLeague();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sezon sıfırlanamadı");
    } finally {
      setResetting(false);
    }
  };

  const openStoryExport = async () => {
    try {
      const response = await api.exportLeagueStory(leagueId);
      setStoryData(response.data);
      setStoryExportOpen(true);
    } catch (error) {
      toast.error("Story verisi yüklenemedi");
    }
  };

  const isLeagueOwner = user && league?.created_by === user.user_id;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#4DFFFF] animate-spin" />
      </div>
    );
  }

  if (!league) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 text-center">
        <p className="text-[#B1C1C1]">Lig bulunamadı</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="league-detail-page">
      {/* Live Update Banner */}
      {liveUpdate && (
        <div className="mb-4 p-3 rounded-lg bg-green-500/20 border border-green-500/50 animate-pulse">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping" />
            <span className="text-green-400 text-sm font-medium">
              Canlı Güncelleme: {liveUpdate.data?.added_by} tarafından Hafta {liveUpdate.data?.week_number} eklendi
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              {league.name}
            </h1>
            {wsConnected && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/20" title="Canlı bağlantı aktif">
                <Wifi className="w-3 h-3 text-green-400" />
                <span className="text-xs text-green-400">CANLI</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4 mt-2">
            <span className="text-[#C6A87C]">{league.game_name}</span>
            <span className="text-[#64748B]">•</span>
            <span className="text-[#B1C1C1]">{league.players?.length || 0} Oyuncu</span>
            <span className="text-[#64748B]">•</span>
            <span className="text-[#B1C1C1]">{league.weeks?.length || 0} Hafta</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Export Buttons */}
          <Button
            onClick={exportAsImage}
            disabled={exporting}
            variant="outline"
            size="sm"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="export-image-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4 mr-1" />}
            PNG
          </Button>
          <Button
            onClick={exportAsPDF}
            disabled={exporting}
            variant="outline"
            size="sm"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="export-pdf-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
            PDF
          </Button>
          <Button
            onClick={sendWhatsAppSummary}
            disabled={sendingWhatsApp}
            variant="outline"
            size="sm"
            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
            data-testid="share-whatsapp-btn"
          >
            {sendingWhatsApp ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-1" />}
            WhatsApp
          </Button>
          <Button
            onClick={() => setShareDialogOpen(true)}
            variant="outline"
            size="sm"
            className="border-[#1DA1F2]/50 text-[#1DA1F2] hover:bg-[#1DA1F2]/10"
            data-testid="social-share-btn"
          >
            <Share2 className="w-4 h-4 mr-1" />
            Paylaş
          </Button>
          <Button
            onClick={openStoryExport}
            variant="outline"
            size="sm"
            className="border-pink-500/50 text-pink-400 hover:bg-pink-500/10"
            data-testid="instagram-story-btn"
          >
            <Instagram className="w-4 h-4 mr-1" />
            Story
          </Button>
          {isLeagueOwner && league.weeks?.length > 0 && (
            <Button
              onClick={() => setResetDialogOpen(true)}
              variant="outline"
              size="sm"
              className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
              data-testid="reset-season-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Sezon Sıfırla
            </Button>
          )}
          {seasons.length > 0 && (
            <Button
              onClick={() => setShowSeasons(!showSeasons)}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="show-seasons-btn"
            >
              <History className="w-4 h-4 mr-1" />
              Arşiv ({seasons.length})
            </Button>
          )}
          {isAdmin && (
            <Link to={`/league/${leagueId}/week/new`}>
              <Button className="bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 font-['Barlow_Condensed'] uppercase tracking-wider font-bold" size="sm">
                <Plus className="w-4 h-4 mr-1" />
                Yeni Hafta
              </Button>
            </Link>
          )}
          {league?.online_enabled && (
            <Button 
              onClick={handleStartOnlineGame}
              className="bg-gradient-to-r from-[#22C55E] to-[#16A34A] text-white hover:from-[#16A34A] hover:to-[#15803D] font-['Barlow_Condensed'] uppercase tracking-wider font-bold animate-pulse" 
              size="sm"
              data-testid="online-play-btn"
            >
              <Gamepad2 className="w-4 h-4 mr-1" />
              Online Oyna
            </Button>
          )}
          {/* 101 Okey ligi ama online aktif değilse bilgi göster */}
          {!league?.online_enabled && (league?.game_type === "101 Okey" || league?.game_id === "okey-101") && isAdmin && (
            <Button 
              onClick={async () => {
                try {
                  await api.enableOnlineMode(leagueId);
                  toast.success("Online mod aktifleştirildi!");
                  fetchLeague();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Online mod aktifleştirilemedi");
                }
              }}
              variant="outline"
              className="border-[#22C55E]/50 text-[#22C55E] hover:bg-[#22C55E]/10 font-['Barlow_Condensed'] uppercase tracking-wider font-bold" 
              size="sm"
            >
              <Gamepad2 className="w-4 h-4 mr-1" />
              Online Aktifleştir
            </Button>
          )}
        </div>
      </div>

      {/* Archived Seasons */}
      {showSeasons && seasons.length > 0 && (
        <div className="glass-card mb-6 overflow-hidden animate-fade-in-up">
          <div className="bg-gradient-to-r from-[#0A1635] to-[#0F1F45] px-6 py-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#C6A87C]" />
              <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                Geçmiş Sezonlar
              </h2>
            </div>
          </div>
          <div className="p-4 space-y-3">
            {seasons.map((season) => (
              <div key={season.archive_id} className="p-4 rounded-lg bg-white/5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-white">Sezon {season.season_number}</h3>
                  <span className="text-xs text-[#64748B]">
                    {new Date(season.archived_at).toLocaleDateString("tr-TR")} • {season.total_weeks} hafta
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {season.final_standings?.slice(0, 4).map((player, idx) => (
                    <div key={player.user_id} className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/5">
                      <span className={`text-xs font-bold ${
                        idx === 0 ? "text-[#C6A87C]" : idx === 1 ? "text-gray-400" : idx === 2 ? "text-[#CD7F32]" : "text-white"
                      }`}>
                        {idx + 1}.
                      </span>
                      <span className="text-sm text-white">{player.name}</span>
                      <span className="text-xs text-[#4DFFFF]">{player.total_points}p</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Standings */}
        <div className="lg:col-span-2">
          <div className="glass-card overflow-hidden" ref={standingsRef}>
            <div className="bg-gradient-to-r from-[#0A1635] to-[#0F1F45] px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[#C6A87C]" />
                <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                  Puan Durumu
                </h2>
              </div>
            </div>
            
            {league.standings?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="league-table">
                  <thead>
                    <tr>
                      <th className="text-left w-16">Sıra</th>
                      <th className="text-left">Oyuncu</th>
                      <th className="text-center">O</th>
                      <th className="text-center">P</th>
                      <th className="text-center" title="Averaj (Ort. Skor)">Avg</th>
                      <th className="text-center">1.</th>
                      <th className="text-center">2.</th>
                      <th className="text-center">3.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {league.standings.map((player, index) => (
                      <tr key={player.user_id}>
                        <td>
                          <span className={`rank-badge rank-${index + 1}`}>{index + 1}</span>
                        </td>
                        <td>
                          <Link 
                            to={`/profile/${player.username}`}
                            className="flex items-center gap-3 hover:text-[#4DFFFF]"
                          >
                            <div className="w-8 h-8 rounded-full bg-[#4DFFFF]/20 flex items-center justify-center">
                              <span className="text-sm font-bold text-[#4DFFFF]">
                                {player.name?.charAt(0)}
                              </span>
                            </div>
                            <span className="text-white">{player.name}</span>
                          </Link>
                        </td>
                        <td className="text-center text-[#B1C1C1]">{player.weeks_played}</td>
                        <td className="text-center">
                          <span className="text-xl font-bold text-[#4DFFFF]">{player.total_points}</span>
                        </td>
                        <td className="text-center text-[#98D8C8]">{player.averaj || 0}</td>
                        <td className="text-center text-[#C6A87C]">{player.first_places}</td>
                        <td className="text-center text-[#A0A0A0]">{player.second_places}</td>
                        <td className="text-center text-[#CD7F32]">{player.third_places}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Trophy className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                <p className="text-[#B1C1C1]">Henüz hafta kaydı yok</p>
              </div>
            )}
          </div>
          
          {/* Stats Button */}
          <div className="flex justify-end mt-4">
            <Link to={`/league/${leagueId}/stats`}>
              <Button className="bg-[#C6A87C] hover:bg-[#B8956E] text-[#050E2E] gap-2">
                <BarChart3 className="w-4 h-4" />
                İstatistikler
              </Button>
            </Link>
          </div>
          
          {/* Performance Graph */}
          {league.weeks?.length > 0 && (
            <div className="mt-6">
              <LeaguePerformanceGraph
                chartData={statsData?.chart_data || []}
                players={statsData?.players || []}
                loading={statsLoading}
              />
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* All Weeks */}
          <div className="glass-card overflow-hidden">
            <div className="bg-gradient-to-r from-[#0A1635] to-[#0F1F45] px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                Tüm Haftalar ({league.weeks?.length || 0})
              </h3>
            </div>
            
            {league.weeks?.length > 0 ? (
              <div className="divide-y divide-white/5 max-h-[400px] overflow-y-auto">
                {league.weeks.map((week) => (
                  <Link
                    key={week.week_id}
                    to={`/league/${leagueId}/week/${week.week_id}`}
                    className="flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#4DFFFF]/20 flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-[#4DFFFF]" />
                      </div>
                      <div>
                        <p className="text-sm text-white font-medium">Hafta {week.week_number}</p>
                        <p className="text-xs text-[#64748B]">{week.date}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#64748B]" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center">
                <Calendar className="w-8 h-8 text-[#64748B] mx-auto mb-2" />
                <p className="text-sm text-[#B1C1C1]">Henüz hafta yok</p>
              </div>
            )}
          </div>

          {/* Championship Probability */}
          {probability && probability.probabilities?.length > 0 && (
            <div className="glass-card overflow-hidden">
              <div className="bg-gradient-to-r from-[#0A1635] to-[#0F1F45] px-4 py-3 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <Percent className="w-4 h-4 text-[#C6A87C]" />
                  <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                    Şampiyonluk Olasılığı
                  </h3>
                </div>
              </div>
              <div className="p-4 space-y-3">
                {probability.probabilities.map((player, idx) => (
                  <div key={player.user_id} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold ${
                          idx === 0 ? "text-[#C6A87C]" : "text-[#64748B]"
                        }`}>
                          {idx + 1}.
                        </span>
                        <span className="text-sm text-white">{player.name}</span>
                        {player.trend === "up" && (
                          <TrendingUp className="w-3 h-3 text-green-400" />
                        )}
                      </div>
                      <span className={`text-sm font-bold ${
                        idx === 0 ? "text-[#C6A87C]" : "text-white"
                      }`}>
                        %{player.probability}
                      </span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          idx === 0 ? "bg-[#C6A87C]" : idx === 1 ? "bg-[#4DFFFF]" : "bg-white/30"
                        }`}
                        style={{ width: `${player.probability}%` }}
                      />
                    </div>
                  </div>
                ))}
                {probability.points_gap > 0 && (
                  <p className="text-xs text-[#64748B] mt-2">
                    Lider {probability.points_gap} puan önde
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Players */}
          <div className="glass-card overflow-hidden">
            <div className="bg-gradient-to-r from-[#0A1635] to-[#0F1F45] px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                Oyuncular
              </h3>
            </div>
            <div className="p-4">
              <div className="flex flex-wrap gap-2">
                {league.players?.map((player) => (
                  <Link
                    key={player.user_id}
                    to={`/profile/${player.username}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <div className="w-6 h-6 rounded-full bg-[#C6A87C]/20 flex items-center justify-center">
                      <span className="text-xs font-bold text-[#C6A87C]">
                        {player.name?.charAt(0)}
                      </span>
                    </div>
                    <span className="text-sm text-white">{player.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reset Season Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="bg-[#0A1635] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white font-['Barlow_Condensed'] uppercase tracking-wider">
              Sezonu Sıfırla
            </DialogTitle>
            <DialogDescription className="text-[#B1C1C1]">
              Mevcut sezon arşivlenecek ve yeni bir sezon başlayacak. Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/30">
              <p className="text-orange-400 text-sm">
                <strong>Dikkat:</strong> {league?.weeks?.length || 0} haftalık veri arşivlenecek. 
                Puan durumu sıfırlanacak ve yeni sezon başlayacak.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setResetDialogOpen(false)}
              className="border-white/20 text-white"
            >
              İptal
            </Button>
            <Button
              onClick={handleResetSeason}
              disabled={resetting}
              className="bg-orange-500 text-white hover:bg-orange-600"
            >
              {resetting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Sezonu Sıfırla"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Social Share Dialog */}
      {shareDialogOpen && (
        <SocialShare
          type="league"
          id={leagueId}
          title={league?.name}
          onClose={() => setShareDialogOpen(false)}
        />
      )}

      {/* Instagram Story Export */}
      <InstagramStoryExport
        type="league"
        data={storyData}
        isOpen={storyExportOpen}
        onClose={() => setStoryExportOpen(false)}
      />
    </div>
  );
}
